<template>
  <div>
    <h2
      class="font-[Poppins] font-black text-[clamp(24px,3.5vw,34px)] text-[#191c1e] mb-1.5 tracking-[-0.5px]"
    >
      Receptsteg
    </h2>
    <p
      class="font-[Plus_Jakarta_Sans] text-[15px] text-[#6c7a77] leading-[1.65] mb-7 max-w-[580px]"
    >
      Numrerat instruktionssteg i receptguiden. Det stora, tonade stegnumret
      (teal, 20&nbsp;% opacity) skapar visuell rytm. Titel och brödtext placeras
      till höger i en flexrad. På mobil visas relevanta ingredienser inline
      under brödtexten.
    </p>

    <PreviewFull label="Steg 1 — grundstruktur">
      <div class="flex gap-8 items-start max-w-[580px]">
        <!-- Faded step number -->
        <div class="shrink-0 opacity-20">
          <span
            class="font-[Poppins] font-extrabold text-[72px] text-teal-500 leading-[72px]"
            >01</span
          >
        </div>
        <div class="flex flex-col gap-3 pt-1 min-w-0 flex-1">
          <h3
            class="font-[Poppins] font-bold text-2xl text-[#191c1e] m-0 leading-8"
          >
            Blanda mjöl och salt
          </h3>
          <p
            class="font-[Plus_Jakarta_Sans] text-xl text-[#3c4947] leading-[32.5px] m-0"
          >
            Väg upp mjölet och blanda det med saltet i en stor bunke. Gör en
            grop i mitten för vätskan.
          </p>
        </div>
      </div>
    </PreviewFull>

    <PreviewFull label="Mobil — ingrediensruta under texten">
      <div class="flex gap-4 items-start max-w-[340px]">
        <div class="shrink-0 opacity-20">
          <span
            class="font-[Poppins] font-extrabold text-[56px] text-teal-500 leading-[56px]"
            >02</span
          >
        </div>
        <div class="flex flex-col gap-3 pt-1 min-w-0 flex-1">
          <h3
            class="font-[Poppins] font-bold text-xl text-[#191c1e] m-0 leading-7"
          >
            Tillsätt surdeg och vatten
          </h3>
          <p
            class="font-[Plus_Jakarta_Sans] text-[17px] text-[#3c4947] leading-[28px] m-0"
          >
            Häll i surdegen och vattnet och blanda tills allt mjöl är
            inkorporerat.
          </p>
          <!-- Mobile ingredient callout -->
          <div class="rounded-[10px] p-3 bg-[#f2f4f6]">
            <p
              class="font-[Plus_Jakarta_Sans] font-bold text-sm text-[#006b5f] mb-1 m-0"
            >
              ANVÄND NU:
            </p>
            <ul class="list-disc pl-5 m-0">
              <li
                class="font-[Plus_Jakarta_Sans] text-[15px] text-[#191c1e] leading-[1.8]"
              >
                400 g vetemjöl
              </li>
              <li
                class="font-[Plus_Jakarta_Sans] text-[15px] text-[#191c1e] leading-[1.8]"
              >
                280 ml vatten
              </li>
              <li
                class="font-[Plus_Jakarta_Sans] text-[15px] text-[#191c1e] leading-[1.8]"
              >
                150 g aktiv surdeg
              </li>
            </ul>
          </div>
        </div>
      </div>
    </PreviewFull>

    <CodeBlock :tabs="snippetTabs" />
  </div>
</template>

<script setup>
import PreviewFull from "../components/PreviewFull.vue";
import CodeBlock from "../components/CodeBlock.vue";

const htmlCode = `<div class="flex gap-3 md:gap-8 items-start">
  <!-- Tonat stegnummer -->
  <div class="shrink-0 opacity-20">
    <span class="font-[Poppins] font-extrabold text-[72px] text-teal-500 leading-[72px]">
      {{ String(idx + 1).padStart(2, '0') }}
    </span>
  </div>

  <div class="flex flex-col gap-3 pt-1 min-w-0 flex-1">
    <h3 class="font-[Poppins] font-bold text-2xl text-[#191c1e] m-0 leading-8">
      {{ step.title }}
    </h3>
    <div
      class="font-[Plus_Jakarta_Sans] text-xl text-[#3c4947] leading-[32.5px] m-0"
      v-html="step.instructions"
    />

    <!-- Ingrediensruta (endast mobil, om step har ingredienser) -->
    <div
      v-if="step.ingredient_groups.length"
      class="md:hidden rounded-[10px] p-3 bg-[#f2f4f6]"
    >
      <p class="font-[Plus_Jakarta_Sans] font-bold text-sm text-[#006b5f] mb-1">
        ANVÄND NU:
      </p>
      <ul class="list-disc pl-5 m-0">
        <template v-for="group in step.ingredient_groups" :key="group.id">
          <li
            v-for="ingredient in group.ingredients"
            :key="ingredient.id"
            class="font-[Plus_Jakarta_Sans] text-[15px] text-[#191c1e] leading-[1.8]"
          >
            {{ formatIngredient(ingredient) }}
          </li>
        </template>
      </ul>
    </div>
  </div>
</div>`;

const cssCode = `/* Stegnummer */
font-family: 'Poppins', sans-serif;
font-weight: 800;         /* extrabold */
font-size: 72px;
line-height: 72px;
color: #14b8a6;           /* text-teal-500 */
opacity: 0.20;            /* tonad */

/* Stegrubrik (h3) */
font-family: 'Poppins', sans-serif;
font-weight: 700;
font-size: 24px;
line-height: 32px;
color: #191c1e;

/* Brödtext */
font-family: 'Plus Jakarta Sans', sans-serif;
font-size: 20px;
line-height: 32.5px;
color: #3c4947;

/* Ingrediensruta (mobil) */
background: #f2f4f6;
border-radius: 10px;
padding: 12px;

/* "ANVÄND NU:" etikett */
font-family: 'Plus Jakarta Sans', sans-serif;
font-weight: 700;
font-size: 14px;
color: #006b5f;

/* Steg-mellanrum */
gap: 56px;   /* gap-14 */
gap: 64px;   /* gap-16 (desktop) */`;

const snippetTabs = [
  { label: "HTML", code: htmlCode },
  { label: "CSS", code: cssCode, lang: "css" },
];
</script>
